/**
 * Formats a duration value in seconds into a human-readable string with appropriate time units.
 *
 * Converts the input value (in seconds) to the most appropriate unit (seconds, minutes, hours, or days)
 * and returns a formatted string with the converted value and unit suffix.
 *
 * @param value - The duration in seconds to format, or undefined
 * @param decimals - Number of decimal places to show (default: 2)
 * @returns A formatted string with the duration and unit (e.g., "90 s", "1.5 min", "2 h", "1 d"),
 *          or the original value if it's not a number
 *
 * @example
 * ```typescript
 * durationFormatter(90)         // Returns "1.5 mn"
 * durationFormatter(90, 0)      // Returns "2 mn"
 * durationFormatter(3600)       // Returns "1 h"
 * durationFormatter(3661, 1)    // Returns "1 h"
 * durationFormatter(86400)      // Returns "1 d"
 * durationFormatter(undefined)  // Returns undefined
 * ```
 */
export const durationFormatter = (
  value: number | undefined,
  decimals: number = 2
) => {
  if (typeof value !== 'number') {
    return value
  }

  const timeUnits = [
    { factor: 60, suffix: 's' },
    { factor: 60, suffix: 'mn' },
    { factor: 24, suffix: 'h' },
    { factor: Infinity, suffix: 'd' },
  ]

  let convertedValue = value
  let suffixIndex = 0
  while (
    convertedValue >= timeUnits[suffixIndex].factor &&
    suffixIndex < timeUnits.length - 1
  ) {
    convertedValue /= timeUnits[suffixIndex].factor
    suffixIndex++
  }

  return `${parseFloat(convertedValue.toFixed(decimals))} ${timeUnits[suffixIndex].suffix}`
}
